<template>
  <Toolbar v-if="car" class="p-mb-4 p-mt-4">
    <template #start>
      <div style="font-size: 1.4em;" class="p-mr-3" >{{ $t('Parts history') }}</div>
    </template>
    <template #end>
      <SearchInput :disabled="dataTableIsLoading"
                   @change-processed-search-value="changeProcessedSearchValue"
                   v-model="searchData"
                   @show-spinner="isLoading = true"/>
    </template>
  </Toolbar>
  <Spinner v-if="isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :showButtons="false"
                    @sort-items="sortItems"
                    :headers="[
                      {name: 'type', title: '',  width: '30px', sortable: false},
                      {name: 'supplier', title: $t('Supplier'),  width: '', sortable: false},
                      {name: 'code', title: $t('Code'),  width: '', sortable: false},
                      {name: 'name', title: $t('Name'),  width: '', sortable: false},
                      {name: 'purchase_price', title: $t('purchasePriceShortLabel'), sortable: false},
                      {name: 'sell_price', title: $t('sellingPriceShortLabel'), sortable: false},
                      {name: 'order', title: $t('Order'),  width: '', sortable: false},
                      {name: 'created_at', title: $t('Created'),  width: '', sortable: true}]">
    <template v-slot:body>
      <tr v-for="(item, index) of items" :key="index">
        <td class="table-data">
          <i :class="{
            'ti-hummer': Number(item.type) === 1,
            'ti-truck': Number(item.type) === 2,
            'ti-package': Number(item.type) === 3,
            'ti-pencil': Number(item.type) === 4
          }"/>
        </td>
        <td class="table-data">
          <span v-if="item.supplier?.company_name">{{ item.supplier.company_name }}</span>
          <i v-else class="ti-minus"></i>
        </td>
        <td class="table-data">
          <span v-if="item.code">{{ item.code }}</span>
          <i v-else class="ti-minus"></i>
        </td>
        <td class="table-data">
          <span v-if="item.name">{{ item.name }}</span>
          <i v-else class="ti-minus"></i>
        </td>
        <td class="table-data">
          <span>{{ formatCurrency(+item.purchase_price) }}</span>
        </td>
        <td class="table-data">
          <span>{{ formatCurrency(+item.sell_price) }}</span>
        </td>
        <td>
          <span v-if="item.order && item.order.number && item.order.uid">
            <router-link :to="{ path: `/orders/${item.order.uid}` }"
                         draggable="false"
                         :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                         rel="noopener">
              {{ item.order.number }}
            </router-link>
          </span>
          <i v-else class="ti-minus"/>
        </td>
        <td class="table-data table-date-line">
          {{ formatBy(item.created_at, item.creator) }}
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="!itemsArrayIsEmpty"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100,200]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from '@/mixins/formatMixins'
import datatableMixins from '@/mixins/datatableMixins'
import showErrorsMixins from "@/mixins/showErrorsMixins";
import settings from "@/settings";

export default {
  mixins: [ formatMixins, datatableMixins, showErrorsMixins ],
  emits: ['update-items', 'change-customer-status'],
  props: {
    vehicleIsLoading: {
      type: Boolean,
      default: false
    },
    car: {
      type: Object,
      default: null
    },
  },
	data() {
		return {
      fieldsExpand: 'order',
      apiLink: 'car/get-parts-history',
      settings
		}
	},
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('vehicles') === -1 || route.indexOf('parts-history') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    if (!this.$route.params.uid) return
    this.checkRouteQuery()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.car?.id) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('carPartsHistory')
      try {
        // const { status, data, headers } = await httpClient.post(`${this.apiLink}?page=${this.pagination.currentPage}&per-page=${this.pagination.perPage}&sort=${this.sort.direction + this.sort.column}&search=${this.searchData}&expand=${this.fieldsExpand}`, { carId: this.$route.params.id })
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: { carId: this.car.id },
          signal: ctrl?.signal
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.carPartsHistory === ctrl) {
          this._abortors.carPartsHistory = null
        }
      }
    },
	},
}
</script>
<style scoped>
td {
  height: 44px;
}
</style>